import type { Dict } from "../types";

export const calendar: Dict = {
  en: {
    "datetime.noEmployeeCombinationAvailable": "No combination of employees is available for this slot. Try a different time or change one of the employees.",

    // Recurring Bookings
    "recurring.series": "Recurring Series",
    "recurring.appointments": "{count} appointments",
    "recurring.moreAppointments": "+{count} More Appointments",
    "recurring.interval.weekly": "Weekly",
    "recurring.interval.monthly": "Monthly",
    "recurring.interval.everyWeeks": "Every {count} weeks",
    "recurring.interval.everyMonths": "Every {count} months",
    "recurring.interval.at": "at",
    "recurring.approveAll": "Approve All",
    "recurring.rejectAll": "Reject All",
    "recurring.approve": "Approve",
    "recurring.reject": "Reject",
    "recurring.processing": "Processing...",
    "recurring.allApproved": "All {count} appointments approved",
    "recurring.allRejected": "All {count} appointments rejected",

    // DateTimeSelector
    "datetime.title": "Choose Date & Time",
    "datetime.subtitle": "Select your preferred appointment slot",
    "datetime.selectDate": "Select a date",
    "datetime.selectTime": "Select a time",
    "datetime.noSlots": "No slots available",
    "datetime.slotsLoadFailed": "We could not load the available times. Check your connection, then try again.",
    "datetime.slotsRateLimited": "Too many requests from this network. Please wait a minute, then try again.",
    "datetime.selectedTimeGone": "The time you picked is no longer available. Please choose another time.",
    "datetime.noHairdresserAvailable": "No employees available at this time. Please select another time slot.",
    "datetime.errorCheckingAvailability": "Error checking availability. Please try again.",
    "datetime.available": "Available",

    // Recurring booking
    "recurring.button": "Recurring",
    "recurring.buttonOn": "Recurring ON",
    "recurring.pickEndDate": "Pick an end date",

    // BookAppointmentDialog & QuickBookDialog
    "bookDialog.bookAppointment": "Book Appointment",
    "bookDialog.selectClientServiceDetails": "Select client, service, employee, date and time",
    "bookDialog.client": "Client",
    "bookDialog.selectClient": "Select client...",
    "bookDialog.searchClient": "Search client by name or email...",
    "bookDialog.noClientFound": "No client found.",
    "bookDialog.service": "Service",
    "bookDialog.selectService": "Select service",
    "bookDialog.hairdresser": "Employee",
    "bookDialog.selectHairdresser": "Select employee",
    "bookDialog.selectDate": "Select Date",
    "bookDialog.selectTime": "Select Time",
    "bookDialog.pleaseSelectDateHairdresserFirst": "Please select an employee and date first",
    "bookDialog.loadingTimes": "Loading available times...",
    "bookDialog.noAvailableTimes": "No available times for this date",
    "bookDialog.slotsLoadFailed": "Could not load availability. The scheduling service did not answer.",
    "bookDialog.slotsLoadFailedKeptTime": "Time kept: {time}",
    "bookDialog.slotsLoadRetry": "Try again",
    "bookDialog.confirmBooking": "Confirm Booking",
    "bookDialog.updateAppointment": "Update Appointment",
    "bookDialog.updating": "Updating...",
    "bookDialog.editingBanner": "Editing appointment for {name}",
    "bookDialog.clientLocked": "Client (locked)",
    "bookDialog.servicesUpdated": "Services updated successfully",
    "bookDialog.servicesUpdatedDesc": "The appointment has been updated.",
    "bookDialog.cancel": "Cancel",
    "bookDialog.booking": "Booking...",
    "bookDialog.error": "Error",
    "bookDialog.missingInfo": "Missing Information",
    "bookDialog.fillAllFields": "Please fill in all required fields",
    "bookDialog.pleaseSelectDateTimeHairdresser": "Please select a date, time, and employee",
    "bookDialog.success": "Success!",
    "bookDialog.appointmentBooked": "Appointment booked for",
    "bookDialog.at": "at",
    "bookDialog.quickBook": "Quick Book Appointment",
    "bookDialog.bookingFor": "Booking",
    "bookDialog.for": "for",
    "bookDialog.recurring": "Recurring",
    "bookDialog.recurringNeedsTime": "Pick a time first: the series repeats from it.",
    "bookDialog.recurringNeedsSlots": "Repeat options will appear with the available times.",
    "bookDialog.recurringNote":
      "Recurring appointments are created and confirmed immediately. If a single date is unavailable, the whole series is refused.",
    "bookDialog.recurringNotePartial":
      "Recurring appointments are created and confirmed immediately. A date taken by another appointment or by a blocked slot is left out, and the rest of the series is created.",
    "bookDialog.partialRecurringRefused": "slot refused",
    "bookDialog.partialRecurringNothingLeft":
      "None of the dates in this series can be booked. Nothing was created.",
    "bookDialog.overtimeWarning": "This appointment extends past closing time",
    "bookDialog.notificationsPaused": "Notifications are paused for this salon. The client will not receive a confirmation for this appointment, but their reminders will still be sent.",
    "bookDialog.overtimeDetails": "Salon closes at {closingTime}. This booking will run {overtimeMinutes} minutes overtime.",
    "bookDialog.backendConflict.pastClosing": "This service would end at {endsAt}, but the salon closes at {closesAt}. Pick an earlier time, or shorten the service.",
    "bookDialog.backendConflict.crossesMidnight": "This service would start at {startsAt} and run {minutes} minutes, ending at {endsAt} the next day. Bookings cannot cross midnight.",
    "bookDialog.backendConflict.blockedFullDay": "This whole day is blocked for this employee. Pick another day.",
    "bookDialog.backendConflict.blockedSlot": "This time falls inside a blocked period for this employee. Pick another time.",
    "bookDialog.backendConflict.overlap": "This employee already has an appointment that overlaps this time. Pick another time or another employee.",
    "bookDialog.backendConflict.exceptionalClosed": "The salon is exceptionally closed on this date. Pick another day.",
    "bookDialog.backendConflict.beforeOpening": "This time is before the opening hour set for this exceptional date. Pick a later time.",
    "bookDialog.backendConflict.beforeOpeningHours": "This time is before opening ({opensAt}). Pick a later time.",
    "bookDialog.backendConflict.closedDay": "The salon is closed on this day. Pick another day.",
    "bookDialog.confirmOvertime": "I confirm this overtime booking",
    "bookDialog.outOfHoursWarning": "This appointment is outside the employee's hours",
    "bookDialog.outOfHoursDetails": "{employee} works from {start} to {end} that day.",
    "bookDialog.confirmOutOfHours": "I confirm this out-of-hours appointment",
    "bookDialog.recurringCreated": "Recurring series created successfully",
    "bookDialog.appointments": "appointments",
    "bookDialog.statusLabel": "Status",
    "bookDialog.statusConfirmed": "Confirmed",
    "bookDialog.statusPending": "Pending",
    "bookDialog.staffNoteLabel": "Note (optional)",
    "bookDialog.staffNotePlaceholder": "Internal note for this appointment…",
    "bookDialog.staffNoteVisibleToClient": "Visible to client",
    "bookDialog.staffNoteVisibleToClientHint": "If off, the note stays private to your team.",
    "bookDialog.staffNoteBadgeVisible": "Visible to client",
    "bookDialog.staffNoteBadgePrivate": "Private",
    "bookDialog.toast.pending": "Appointment created as pending for",

    // Multi-service booking
    "bookDialog.multiServices": "Multi-Services",
    "bookDialog.multiServicesToggleNo": "No",
    "bookDialog.forfaitDroppedOnExit": "This package needs several services. Leaving multi-service mode drops it: pick the services again, or add the package back.",
    "bookDialog.multiServicesToggleYes": "Yes",
    "bookDialog.selectedServices": "Selected Services",
    "bookDialog.addService": "Add a service",
    "bookDialog.totalDuration": "Total Duration",
    "bookDialog.totalPrice": "Total Price",
    "bookDialog.servicesCount": "services",
    "bookDialog.variableDurationRequired": "Duration required",
    "bookDialog.removeService": "Remove service",
    "bookDialog.serviceDuration": "Duration (min)",
    "bookDialog.maxServicesReached": "Maximum of 5 services reached",
    "bookDialog.pause": "Pause",
    "bookDialog.addPause": "Add pause",
    "bookDialog.removePause": "Remove pause",
    "bookDialog.dnd.handleAriaLabel": "Reorder this item",
    "bookDialog.performedBy": "By",
    "bookDialog.selectEmployeeForService": "Select employee",
    "bookDialog.allEmployeesRequired": "Please select an employee for each service",
    "bookDialog.customerUnresolved": "This client's file is not accessible from your account. Ask the salon owner to grant access.",
    "bookDialog.overtimeServiceDetail": "{service} with {employee} exceeds schedule by {minutes} minutes",
    "bookDialog.employeeEndsAt": "{employee} ends at {time}",
    "bookDialog.missingField": "Select this first: {field}",

    // Additional DateTimeSelector keys
    "datetime.failedToLoadHours": "Failed to load opening hours",
    "datetime.failedToLoadSlots": "Failed to load available time slots",
    "datetime.recurringAppointments": "Recurring Appointments",

    // RecurringBookingDialog
    "recurringDialog.title": "Set Up Recurring Appointments",
    "recurringDialog.description": "Book multiple appointments in advance.",
    "recurringDialog.repeatEvery": "Repeat Every",
    "recurringDialog.week": "Week",
    "recurringDialog.weeks": "Weeks",
    "recurringDialog.month": "Month",
    "recurringDialog.months": "Months",
    "recurringDialog.endDate": "End Date",
    "recurringDialog.availabilityNote":
      "We'll book available slots and suggest alternatives for conflicts. The salon will review your request.",
    "recurringDialog.cancel": "Cancel",
    "recurringDialog.continue": "Continue",
    "recurringDialog.pendingApproval": "All appointments will be pending owner approval.",
    "rebook.dialogTitle": "Pre-book appointment",
    "rebook.dialogSubtitle": "All details from the original appointment are pre-filled. Adjust what you need.",
    "rebook.successToast": "Pre-booking confirmed",
    "Calendar.noOneWorkingTitle": "No one is working this day",
    "Calendar.noOneWorkingHelp": "Columns are hidden because no employee is scheduled and there are no appointments.",
    "Calendar.showEveryone": "Show everyone",
    "Calendar.empty.noEmployeesTitle": "No employees in this salon",
    "Calendar.empty.noEmployeesHelp": "The calendar shows one column per employee. Add an employee to get started.",
    "Calendar.empty.noEmployeesAction": "Add an employee",
    "Calendar.empty.myCalendarOrphanTitle": "Your profile has no calendar column",
    "Calendar.empty.myCalendarOrphanHelp": "My Calendar only shows your own column, and your profile does not have one.",
    "Calendar.empty.myCalendarOrphanAction": "Show the whole salon",
    "Calendar.empty.filterExcludesAllTitle": "A filter is hiding every employee",
    "Calendar.empty.filterExcludesAllHelp": "None of the filtered employees belong to this salon.",
    "Calendar.empty.filterExcludesAllAction": "Show all employees",
    "Calendar.empty.unknownTitle": "Nothing to show",
    "Calendar.empty.unknownHelp": "No column matches the current display.",
    "Calendar.empty.resetAction": "Reset the display",
    "Calendar.empty.loadingLabel": "Loading the calendar",
    "Calendar.empty.rosterUnavailableTitle": "Could not load the employees",
    "Calendar.empty.rosterUnavailableHelp": "The calendar needs the employee list to draw its columns.",
    "Calendar.empty.rosterUnavailableAction": "Try again",
    "Calendar.empty.rosterOfflineTitle": "No connection",
    "Calendar.empty.rosterOfflineHelp": "The calendar will resume on its own as soon as the connection is back.",
    "Calendar.empty.noEmployeesHelpNoAccess": "Ask the owner to add an employee to the salon.",
    "Calendar.empty.myCalendarOrphanHelpLocked": "Your access is limited to your own column. Ask the owner to link your account to a service provider profile.",
    "Calendar.clientNotReached": "Client not reached: SMS not delivered",
    "Calendar.awaitingClient": "Awaiting client confirmation",
    "Calendar.awaitingClientBadge": "SMS sent",

    // DateTime errors
    "datetime.error": "Error",

    // AppointmentCalendar
    "Calendar.daily": "Daily",
    "Calendar.weekly": "Weekly",
    "Calendar.monthly": "Monthly",
    "Calendar.backToView": "Back to {view} view",
    "Calendar.drawerBusyBook": "An appointment is being entered. Finish or cancel it before blocking time.",
    "Calendar.drawerBusyBlock": "A time block is being entered. Finish or cancel it before opening an appointment.",
    "Calendar.slotDuringEditExplain": "You are currently editing an appointment. Abandon that edit to open the new appointment you asked for.",
    "Calendar.slotDuringRebookExplain": "You are currently preparing a pre-booking. Abandon that pre-booking to open the new appointment you asked for.",
    "Calendar.slotDuringEditAbandon": "Abandon and open the new appointment",
    "Calendar.cycleViewTitle": "Switch view (Day / Week / Month)",
    "Calendar.today": "Today",
    "Calendar.hairdressers": "Employees",
    "Calendar.room": "Room:",
    "Calendar.equipment": "Equipment:",
    "Calendar.noAppointments": "No appointments",
    "Calendar.more": "more",
    "Calendar.closed": "CLOSED",
    "Calendar.awayColumnBadge": "Not working this day",
    "Calendar.awayColumnAddedForAppointment": "Added for this appointment",
    "Calendar.exceptionalClosure": "EXCEPTIONAL CLOSURE",
    "Calendar.serviceBlocksSameDayOnly": "Service blocks must stay on the same day as their appointment.",
    "Calendar.cannotReschedule": "Cannot reschedule",
    "Calendar.cannotResize": "Cannot resize",
    "Calendar.invalidTime": "Invalid Time",
    "Calendar.selectTimeBetween7and23": "Please select a time between 7:00 and 23:00",
    "Calendar.appointments": "appts",
    "Calendar.appointmentRescheduled": "Appointment rescheduled",
    "Calendar.appointmentResized": "Appointment resized",
    "Calendar.serviceMoved": "Service moved",
    "Calendar.serviceMovedSuccess": "Service successfully moved to new time",
    "Calendar.failedToMoveService": "Failed to move service",
    "Calendar.invalidMove": "Invalid move",
    "Calendar.serviceCannotStartBeforeAppointment": "Service cannot start before the appointment",
    "Calendar.serviceCannotStartBeforePrevious": "This service cannot start before the previous service ends",
    "Calendar.serviceCannotEndAfterNext": "This service cannot end after the next service starts",
    "Calendar.confirmServiceMove": "Confirm Service Move",
    "Calendar.confirmServiceMoveDescription": "Move this service to a new time within the appointment?",
    "Calendar.segmentMoveNotAllowed": "Cannot move individual segments",
    "Calendar.movingEntireAppointment": "Moving entire appointment instead",
    "Calendar.segmentOverlap": "This position overlaps with another appointment",
    "Calendar.appointmentChangedRetry": "This appointment was just changed. Please try again.",
    "Calendar.overlapWarning": "This slot overlaps with another appointment",
    "Calendar.outOfHoursWarning": "This slot is outside the employee's hours",
    "Calendar.outOfHoursMove": "Outside {employee}'s hours",
    "Calendar.moveService": "Move Service",
    "Calendar.confirmServiceTransfer": "Transfer Service to Another Employee",
    "Calendar.confirmServiceTransferDescription": "Transfer this service to a different employee? A new appointment will be created.",
    "Calendar.transferService": "Transfer Service",
    "Calendar.serviceTransferred": "Service Transferred",
    "Calendar.serviceTransferredTo": "Service transferred to {employee}",
    "Calendar.failedToTransferService": "Failed to transfer service. Please try again.",
    "Calendar.serviceTransferNote": "A new appointment will be created for the selected employee. The service will be removed from the original appointment.",
    "Calendar.lastServiceCannotMove": "Cannot transfer the only service in an appointment. Move the entire appointment instead.",
    "Calendar.cannotMoveService": "Cannot Transfer Service",
    "Calendar.successfullyMoved": "Successfully moved to",
    "Calendar.updated": "Updated from",
    "Calendar.at": "at",
    "Calendar.to": "to",
    "Calendar.from": "From",
    "Calendar.newTime": "New time",
    "Calendar.error": "Error",
    "Calendar.failedToReschedule": "Failed to reschedule appointment. Please try again.",
    "Calendar.noServiceFollowsEmployee": "No active service is assigned to this employee, so there is nothing to move.",
    "Calendar.movePartiallyApplied": "The appointment was moved, but reassigning its services failed. Please check the appointment.",
    "Calendar.changeNotAppliedSaveInProgress": "A save was in progress, so this change was not applied. Please try again.",
    "Calendar.failedToResize": "Failed to resize appointment. Please try again.",
    "Calendar.serviceWouldOverlapNext": "This resize would cause the service to overlap with the next service.",
    "Calendar.serviceWouldOverlapPrevious": "This resize would cause the service to overlap with the previous service.",
    "Calendar.employeeChange": "Employee Change",
    "Calendar.reassignedTo": "Reassigned to",
    "Calendar.confirmReschedule": "Confirm Reschedule",
    "Calendar.confirmResize": "Confirm Resize",
    "Calendar.confirmRescheduleQuestion": "Are you sure you want to reschedule this appointment?",
    "Calendar.confirmResizeQuestion": "Are you sure you want to resize this appointment?",
    "Calendar.resizeNewAppointmentStart": "The appointment will start at {time}.",
    "Calendar.confirmBlockMove": "Move block?",
    "Calendar.confirmBlockMoveQuestion": "Confirm moving this time block?",
    "Calendar.confirmBlockResize": "Resize block?",
    "Calendar.confirmBlockResizeQuestion": "Confirm the new duration of this time block?",
    "Calendar.service": "Service",
    "Calendar.customer": "Customer",
    "Calendar.cancel": "Cancel",
    "Calendar.notifDispatchFailed": "Customer notification could not be sent.",
    "Calendar.confirm": "Confirm",
    "Calendar.blockTime": "Block Time",
    "Calendar.blockedTimeManagement": "Manage Blocked Time",
    "Calendar.reason": "Reason",
    "Calendar.add": "Add",
    "Calendar.newAppointment": "New Appointment",
    "Calendar.myCalendarToggle": "My Calendar",
    "Calendar.showAllEmployees": "All Employees",
    "Calendar.compactView": "Compact View",
    "Calendar.expandedView": "Expanded View",
    "Calendar.quickStats": "Quick Stats",
    "Calendar.weekDays.sun": "SUN",
    "Calendar.weekDays.mon": "MON",
    "Calendar.weekDays.tue": "TUE",
    "Calendar.weekDays.wed": "WED",
    "Calendar.weekDays.thu": "THU",
    "Calendar.weekDays.fri": "FRI",
    "Calendar.weekDays.sat": "SAT",
    "Calendar.overflowMenu.period": "View",
    "Calendar.overflowMenu.myCalendar": "My Calendar",
    "Calendar.overflowMenu.quickStats": "Quick Stats",
    "Calendar.appointmentCount": "Appointments",
    "Calendar.totalRevenue": "Revenue",
    "Calendar.serviceSales": "Service sales",
    "Calendar.productSales": "Product sales",
    "Calendar.averageTicket": "Avg. Ticket",
    "Calendar.occupationRate": "Occupancy",
    "Calendar.thisWeek": "This Week",
    "Calendar.thisMonth": "This Month",
    "Calendar.cancelledNoReschedule": "Cancelled appointments cannot be rescheduled",
    "Calendar.pastNoReschedule": "Past appointments cannot be rescheduled",
    "Calendar.salonClosedDay": "Salon is closed on this day",
    "Calendar.hairdresserNotWorking": "This employee is not working on this day",
    "Calendar.timeBlocked": "Time slot is blocked",
    "Calendar.timeOverlaps": "Time slot overlaps with another appointment",
    "Calendar.employeeAlreadyBooked": "already has an appointment at this time",
    "Calendar.unknownService": "Unknown Service",
    "Calendar.shareSchedule": "Share schedule",
    "Calendar.editDay": "Edit",
    "Calendar.viewOnlyEmployee": "View only {name}",
    "Calendar.viewAllEmployees": "View everyone",
    "Calendar.showAllStatuses": "All statuses",
    "Calendar.scheduleShared": "Schedule shared",
    "Calendar.shareError": "Failed to share schedule",
    "Calendar.clickToBook": "Click to book at",
    "Calendar.slotChoiceAppointment": "Appointment",
    "Calendar.slotChoiceBlock": "Block time",
    "Calendar.slotChoiceAppointmentHint": "Client, service, time",
    "Calendar.slotChoiceBlockHint": "Time off, lunch, training",
    "Calendar.slotChoiceTitle": "What do you want to add?",
    "Calendar.blockRoom": "Block this room",
    "Calendar.blockRoomDescription": "The room will be closed for the selected range. No appointment can be booked in it, from the dashboard or from online booking.",
    "Calendar.notes": "Notes:",
    "Calendar.pauseOccupied": "Pause Period (Occupied)",
    "Calendar.pauseBookedMsg": "Part of appointment: currently has another booking",
    "Calendar.availableSlot": "Available Time Slot",
    "Calendar.partOfAppointment": "Part of appointment",
    "Calendar.clickDuringPause": "Click to book during this pause period",
    "Calendar.workBlock": "Work Block",
    "Calendar.rescheduleSuccess": "Appointment rescheduled successfully",
    "Calendar.rescheduleError": "Failed to reschedule appointment",
    "Calendar.resizeSuccess": "Appointment duration changed successfully",
    "Calendar.resizeError": "Failed to change appointment duration",
    "Calendar.editAppointment": "Edit Appointment",
    "Calendar.hairdresser": "Employee",
    "Calendar.selectHairdresser": "Select an employee",
    "Calendar.date": "Date",
    "Calendar.duration": "Duration",
    "Calendar.time": "Time",
    "Calendar.save": "Save",
    "Calendar.saving": "Saving...",
    "Calendar.success": "Success",
    "Calendar.appointmentUpdated": "Appointment updated successfully",
    "Calendar.noAvailableSlots": "No available time slots",
    "Calendar.fillAllFields": "Please fill in all required fields",
    "Calendar.errorFetchingSlots": "Unable to fetch available time slots",
    "Calendar.errorUpdating": "Unable to update appointment",
    "Calendar.multiServiceAppointment": "Multi-service appointment",
    "Calendar.mobileMove": "Move",
    "Calendar.mobileMoveService": "Move this service",
    "Calendar.mobileMoveSegment": "Move this block",
    "Calendar.mobileDuration": "Duration",
    "Calendar.mobileDurationService": "Duration of this service",
    "Calendar.mobileDurationSegment": "Duration of this block",
    "Calendar.mobileAdjust": "Adjust",
    "Calendar.mobileWas": "was",
    "Calendar.mobileSelected": "Selected",
    "Calendar.mobileScopeServiceSubtitle": "Service {index} of {total}: {name}",
    "Calendar.mobileScopeSegmentSubtitle": "Block {index} of {total}: {name}",
    "Calendar.mobileEditFullAppointment": "Edit full appointment",
    "Calendar.mobileCancelFullAppointment": "Cancel full appointment",
    "Calendar.employees": "Employees",
    "Calendar.appointmentStatus": "Appointment status",
    "Calendar.weekJump": "Weeks",
    "Calendar.statusConfirmed": "Confirmed",
    "Calendar.statusPending": "Pending",
    "Calendar.statusCancelled": "Cancelled",
    "Calendar.statusNoShow": "No-show",
    "Calendar.statusPresent": "Present",
    "Calendar.toggleSidebar": "Side panel",
    "Calendar.groupByEmployee": "By employee",
    "Calendar.groupByRoom": "By room",
    "Calendar.unassignedRoom": "Unassigned",
    "Calendar.moveToRoom": "Room",
    "Calendar.roomChangeRefused": "The room could not be changed ({reason}). The appointment stays in its original room.",
    "Calendar.workstationsShort": "stations",

    // BlockedSlots
    "BlockedSlots.addTitle": "Block Time Slot",
    "BlockedSlots.editTitle": "Edit Blocked Time",
    "BlockedSlots.dialogDescription":
      "Block a full day or one or more time ranges, for the whole salon or for specific employees.",
    "BlockedSlots.hairdresser": "Employee",
    "BlockedSlots.allHairdressers": "All Employees (Salon-wide)",
    "BlockedSlots.allHairdressersCheckbox": "Whole salon",
    "BlockedSlots.selectAtLeastOne": "Select at least one employee",
    "BlockedSlots.date": "Date",
    "BlockedSlots.pickDate": "Pick a date",
    "BlockedSlots.timeSlots": "Time Slots",
    "BlockedSlots.addTimeSlot": "Add Time Slot",
    "BlockedSlots.slot": "Time Slot",
    "BlockedSlots.startTime": "Start Time",
    "BlockedSlots.endTime": "End Time",
    "BlockedSlots.selectTime": "Select time",
    "BlockedSlots.reason": "Reason",
    "BlockedSlots.optional": "optional",
    "BlockedSlots.reasonPlaceholder": "e.g., Doctor appointment, Training, etc.",
    "BlockedSlots.fillAllFields": "Please fill in all required fields",
    "BlockedSlots.invalidTimeRange": "End time must be after start time",
    "BlockedSlots.closedDay": "The salon/employee is closed on this day",
    "BlockedSlots.outsideHours": "The selected time is outside of working hours. Valid hours:",
    "BlockedSlots.createSuccess": "Time slot blocked successfully",
    "BlockedSlots.createSuccessMultiple": "time slots blocked successfully",
    "BlockedSlots.updateSuccess": "Blocked time slot updated",
    "BlockedSlots.deleteSuccess": "Blocked time slot removed",
    "BlockedSlots.saveError": "Failed to save blocked time slot",
    "BlockedSlots.deleteError": "Failed to delete blocked time slot",
    "BlockedSlots.allLocationsNote": "This block applies in all of this person's locations.",
    "BlockedSlots.notAppliedIn": "This block could not be applied in: {locations}.",
    "BlockedSlots.checkAccess": "Check your access to these locations.",
    "BlockedSlots.copiesNotChecked": "This person's other locations could not be checked: this block may not apply there.",
    "BlockedSlots.siblingAppointmentsWarning": "This person has {count} appointment(s) in their other locations during this block. Block anyway?",
    "BlockedSlots.moveSuccess": "Block moved",
    "BlockedSlots.moveError": "Error moving the block",
    "BlockedSlots.resizeSuccess": "Block duration updated",
    "BlockedSlots.resizeError": "Error resizing the block",
    "BlockedSlots.overlapRefused": "Cannot place: this block would overlap an appointment",
    "BlockedSlots.conflictCheckError": "Could not check for overlapping appointments. Check your connection and try again.",
    "BlockedSlots.invalidRange": "Invalid time range",
    "BlockedSlots.blocked": "Blocked",
    "BlockedSlots.blocksInRange": "blocks in this range",
    "BlockedSlots.blocksCount": "blocks",
    "BlockedSlots.noReason": "No reason",
    "BlockedSlots.salonClosed": "Salon Closed",
    "BlockedSlots.blockedTime": "Blocked Time Slot",
    "BlockedSlots.salonBlocked": "Salon-wide Block",
    "BlockedSlots.fullDay": "Block entire day",
    "BlockedSlots.fullDayDescription": "Automatically block all working hours",
    "BlockedSlots.willBlock": "Will block:",
    "BlockedSlots.willBlockMultiple": "Will block for each employee:",
    "BlockedSlots.recurring": "Recurring",
    "BlockedSlots.recurringDescription": "Create recurring blocks on multiple dates",
    "BlockedSlots.repeatEvery": "Repeat every",
    "BlockedSlots.week": "Week",
    "BlockedSlots.weeks": "Weeks",
    "BlockedSlots.month": "Month",
    "BlockedSlots.months": "Months",
    "BlockedSlots.untilDate": "Until",
    "BlockedSlots.pickEndDate": "Pick end date",
    "BlockedSlots.maxDurationAhead": "Maximum {duration} ahead",
    "BlockedSlots.blockWillBeCreated": "block will be created",
    "BlockedSlots.blocksWillBeCreated": "blocks will be created",
    "BlockedSlots.groupManagement": "Blocked Time Group",
    "BlockedSlots.groupDialogDescription":
      "Every line in this group. Select the ones you want to remove, or edit one on its own.",
    "BlockedSlots.employeesBlocked": "Blocked Employees",
    "BlockedSlots.selectAllForDay": "Select entire day",
    "BlockedSlots.deleteSelected": "Delete Selected",
    "BlockedSlots.deleteSuccessMultiple": "blocked time slots deleted",
    "BlockedSlots.confirmDeleteTitle": "Confirm Deletion",
    "BlockedSlots.confirmDeleteSelectedDescription": "Are you sure you want to delete the selected blocked time slots?",
    "BlockedSlots.heterogeneousWarning": "Hours differ between the lines below. Saving will apply the same hours to all of them.",
    "BlockedSlots.startDate": "From",
    "BlockedSlots.endDate": "To",
    "BlockedSlots.multiDayPreview": "{count} days will be blocked",
    "BlockedSlots.multiDaySummary": "{count} days blocked, from {start} to {end}",
    "BlockedSlots.dateRangeWarning": "Some days in this range have existing appointments",
    "BlockedSlots.advancedOptions": "Advanced Options",
    "BlockedSlots.repeatLabel": "Repetition",
    "BlockedSlots.repeatSingle": "This day only",
    "BlockedSlots.repeatConsecutive": "Consecutive days",
    "BlockedSlots.repeatRecurring": "Recurrence",
    "BlockedSlots.deleteConfirmTitle": "Delete this block?",
    "BlockedSlots.deleteConfirmDescription": "This action cannot be undone.",

    // Calendar
    "Calendar.showAll": "All Employees",
    "Calendar.myCalendar": "My Calendar",

    // Daily Notes
    "DailyNote.title": "Daily Note",
    "DailyNote.placeholder": "e.g., Late arrival, Sick child...",
    "DailyNote.save": "Save",
    "DailyNote.clear": "Clear",
    "DailyNote.saved": "Note saved",
    "DailyNote.cleared": "Note cleared",
    "DailyNote.error": "Failed to save note",
    "Calendar.colorByRoom": "By Room",
    "pendingDialog.expiredTitle": "Expired requests",
    "pendingDialog.expiredHint": "These appointments are in the past and can no longer be approved. They are not counted in the notification badge.",
    "pendingDialog.expiredDismiss": "Decline",
    "cancellationLog.windowHint": "Cancellations from the last 90 days that you have not marked as seen. The full history lives in Reports.",
    "cancellationLog.loadFailed": "Could not load the cancellations. Please try again.",

    // Cancel
    "cancel.noteLabel": "Cancellation note",
    "cancel.notePlaceholder": "Reason for cancellation or follow-up note...",
    "cancel.noteVisibleToClient": "Visible to client (included in cancellation email)",

    // Cancellation Log
    "cancellationLog.title": "Cancelled Appointments",
    "cancellationLog.markAsSeen": "Mark as seen",
    "cancellationLog.markAllAsSeen": "Mark all as seen",
    "cancellationLog.noItems": "No unacknowledged cancellations",
    "cancellationLog.cancelledBy": "Cancelled by",
    "cancellationLog.cancelledAt": "Cancelled on",
    "cancellationLog.internalNote": "Internal note",
    "cancellationLog.cancelledByClient": "client",
    "cancellationLog.cancelledByOwner": "owner",
    "cancellationLog.cancelledByEmployee": "employee",
    "cancellationLog.cancelledByVoiceAI": "voice assistant",
    "cancellationLog.byNamed": "Cancelled by {name} ({role})",
    "cancellationLog.byOwner": "Cancelled by the owner",
    "cancellationLog.byEmployee": "Cancelled by an employee",
    "cancellationLog.byClient": "Cancelled by the client",
    "cancellationLog.byVoiceAI": "Cancelled by the voice assistant",
    "cancellationLog.bySystem": "Cancelled automatically (deposit not received in time)",
    "cancellationLog.byCascade": "Cancelled by the salon (last service removed)",
    "cancellationLog.byRaw": "Cancelled by {actor}",
    "cancellationLog.whoNamed": "{name} ({role})",
    "cancellationLog.whoOwner": "Owner",
    "cancellationLog.whoEmployee": "Employee",
    "cancellationLog.whoClient": "Client",
    "cancellationLog.whoVoiceAI": "Voice assistant",
    "cancellationLog.whoSystem": "Automatic (deposit not received in time)",
    "cancellationLog.whoCascade": "Salon (last service removed)",
    "cancellationLog.roleOwner": "owner",
    "cancellationLog.roleEmployee": "employee",
    "cancellationLog.roleClient": "client",
    "cancellationLog.showOnCalendar": "Show on calendar",
    "cancellationLog.shownOnCalendar": "Appointment shown on the calendar again",
    "cancellationLog.hiddenBadge": "Hidden from calendar",
    "cancellationLog.showOnCalendarFailed": "Could not show the appointment. Please try again.",

    // Booking drawer shell
    "Calendar.drawerClose": "Close",
    "Calendar.drawerPeek": "Preview",
    "Calendar.drawerHalf": "Half screen",
    "Calendar.drawerFull": "Full screen",
  },
  fr: {
    "datetime.noEmployeeCombinationAvailable": "Aucune combinaison d'employé(e)s n'est disponible pour ce créneau. Essayez une autre heure ou changez un(e) employé(e).",

    // Recurring Bookings
    "recurring.series": "Série récurrente",
    "recurring.appointments": "{count} rendez-vous",
    "recurring.moreAppointments": "+{count} autres rendez-vous",
    "recurring.interval.weekly": "Hebdomadaire",
    "recurring.interval.monthly": "Mensuel",
    "recurring.interval.everyWeeks": "Toutes les {count} semaines",
    "recurring.interval.everyMonths": "Tous les {count} mois",
    "recurring.interval.at": "à",
    "recurring.approveAll": "Tout approuver",
    "recurring.rejectAll": "Tout rejeter",
    "recurring.approve": "Approuver",
    "recurring.reject": "Rejeter",
    "recurring.processing": "Traitement...",
    "recurring.allApproved": "Tous les {count} rendez-vous approuvés",
    "recurring.allRejected": "Tous les {count} rendez-vous rejetés",

    // DateTimeSelector
    "datetime.title": "Choisissez la date et l'heure",
    "datetime.subtitle": "Sélectionnez votre créneau horaire préféré",
    "datetime.selectDate": "Sélectionnez une date",
    "datetime.selectTime": "Sélectionnez une heure",
    "datetime.noSlots": "Aucun créneau disponible",
    "datetime.slotsLoadFailed": "Impossible de charger les heures disponibles. Vérifiez votre connexion, puis réessayez.",
    "datetime.slotsRateLimited": "Trop de demandes depuis ce réseau. Patientez une minute, puis réessayez.",
    "datetime.selectedTimeGone": "L'heure choisie n'est plus disponible. Choisissez une autre heure.",
    "datetime.noHairdresserAvailable":
      "Aucun employé disponible à cette heure. Veuillez sélectionner un autre créneau horaire.",
    "datetime.errorCheckingAvailability": "Erreur lors de la vérification de la disponibilité. Veuillez réessayer.",
    "datetime.available": "Disponible",
    "datetime.failedToLoadHours": "Échec du chargement des heures d'ouverture",
    "datetime.failedToLoadSlots": "Échec du chargement des créneaux horaires disponibles",
    "datetime.recurringAppointments": "Rendez-vous récurrents",

    // Recurring booking
    "recurring.button": "Récurrent",
    "recurring.buttonOn": "Récurrent ACTIVÉ",
    "recurring.pickEndDate": "Choisir une date de fin",

    // RecurringBookingDialog
    "recurringDialog.title": "Configurer les rendez-vous récurrents",
    "recurringDialog.description": "Réservez plusieurs rendez-vous à l'avance.",
    "recurringDialog.repeatEvery": "Répéter chaque",
    "recurringDialog.week": "Semaine",
    "recurringDialog.weeks": "Semaines",
    "recurringDialog.month": "Mois",
    "recurringDialog.months": "Mois",
    "recurringDialog.endDate": "Date de fin",
    "recurringDialog.availabilityNote":
      "Nous réserverons les créneaux disponibles et proposerons des alternatives en cas de conflit. Le salon examinera votre demande.",
    "recurringDialog.cancel": "Annuler",
    "recurringDialog.continue": "Continuer",
    "recurringDialog.pendingApproval": "Tous les rendez-vous seront en attente d'approbation du propriétaire.",
    "rebook.dialogTitle": "Pré-réserver un rendez-vous",
    "rebook.dialogSubtitle": "Tous les détails du rendez-vous original sont pré-remplis. Ajustez ce dont vous avez besoin.",
    "rebook.successToast": "Pré-réservation confirmée",
    "Calendar.noOneWorkingTitle": "Personne ne travaille ce jour-là",
    "Calendar.noOneWorkingHelp": "Les colonnes sont masquées : aucun employé n'est prévu et il n'y a aucun rendez-vous.",
    "Calendar.showEveryone": "Afficher tout le monde",
    "Calendar.empty.noEmployeesTitle": "Aucun employé dans ce salon",
    "Calendar.empty.noEmployeesHelp": "Le calendrier affiche une colonne par employé. Ajoutez un employé pour commencer.",
    "Calendar.empty.noEmployeesAction": "Ajouter un employé",
    "Calendar.empty.myCalendarOrphanTitle": "Votre fiche n'a pas de colonne au calendrier",
    "Calendar.empty.myCalendarOrphanHelp": "Le mode Mon calendrier ne montre que votre propre colonne, et votre fiche n'en a pas.",
    "Calendar.empty.myCalendarOrphanAction": "Voir tout le salon",
    "Calendar.empty.filterExcludesAllTitle": "Un filtre masque tous les employés",
    "Calendar.empty.filterExcludesAllHelp": "Aucun employé retenu par le filtre ne fait partie de ce salon.",
    "Calendar.empty.filterExcludesAllAction": "Afficher tous les employés",
    "Calendar.empty.unknownTitle": "Rien à afficher",
    "Calendar.empty.unknownHelp": "Aucune colonne ne correspond à l'affichage actuel.",
    "Calendar.empty.resetAction": "Réinitialiser l'affichage",
    "Calendar.empty.loadingLabel": "Chargement du calendrier",
    "Calendar.empty.rosterUnavailableTitle": "Impossible de charger les employés",
    "Calendar.empty.rosterUnavailableHelp": "Le calendrier a besoin de la liste des employés pour afficher ses colonnes.",
    "Calendar.empty.rosterUnavailableAction": "Réessayer",
    "Calendar.empty.rosterOfflineTitle": "Pas de connexion",
    "Calendar.empty.rosterOfflineHelp": "Le calendrier reprendra tout seul dès que la connexion reviendra.",
    "Calendar.empty.noEmployeesHelpNoAccess": "Demandez au propriétaire d'ajouter un employé au salon.",
    "Calendar.empty.myCalendarOrphanHelpLocked": "Votre accès est limité à votre propre colonne. Demandez au propriétaire de lier votre compte à une fiche de prestataire.",
    "Calendar.clientNotReached": "Client non joint : SMS non délivré",
    "Calendar.awaitingClient": "En attente de confirmation du client",
    "Calendar.awaitingClientBadge": "SMS envoyé",

    // DateTime errors
    "datetime.error": "Erreur",

    // BookAppointmentDialog
    "bookDialog.bookAppointment": "Réserver un rendez-vous",
    "bookDialog.selectClientServiceDetails": "Sélectionner le client, le service, l'employé(e), la date et l'heure",
    "bookDialog.client": "Client",
    "bookDialog.selectClient": "Sélectionner un client...",
    "bookDialog.searchClient": "Rechercher un client par nom ou courriel...",
    "bookDialog.noClientFound": "Aucun client trouvé.",
    "bookDialog.service": "Service",
    "bookDialog.selectService": "Sélectionner un service",
    "bookDialog.hairdresser": "Employé(e)",
    "bookDialog.selectHairdresser": "Sélectionner un(e) employé(e)",
    "bookDialog.selectDate": "Sélectionner une date",
    "bookDialog.selectTime": "Sélectionner l'heure",
    "bookDialog.pleaseSelectDateHairdresserFirst": "Veuillez d'abord sélectionner le service, l'employé(e) et la date",
    "bookDialog.loadingTimes": "Chargement des heures disponibles...",
    "bookDialog.noAvailableTimes": "Aucune heure disponible pour cette date",
    "bookDialog.slotsLoadFailed": "Impossible de charger les disponibilités. Le service des horaires n'a pas répondu.",
    "bookDialog.slotsLoadFailedKeptTime": "Heure conservée : {time}",
    "bookDialog.slotsLoadRetry": "Réessayer",
    "bookDialog.confirmBooking": "Confirmer la réservation",
    "bookDialog.updateAppointment": "Mettre à jour le rendez-vous",
    "bookDialog.updating": "Mise à jour...",
    "bookDialog.editingBanner": "Modification du rendez-vous pour {name}",
    "bookDialog.clientLocked": "Client (verrouillé)",
    "bookDialog.servicesUpdated": "Services mis à jour",
    "bookDialog.servicesUpdatedDesc": "Le rendez-vous a été mis à jour.",
    "bookDialog.cancel": "Annuler",
    "bookDialog.booking": "Réservation en cours...",
    "bookDialog.error": "Erreur",
    "bookDialog.missingInfo": "Informations manquantes",
    "bookDialog.fillAllFields": "Veuillez remplir tous les champs requis",
    "bookDialog.pleaseSelectDateTimeHairdresser": "Veuillez sélectionner une date, une heure et un(e) employé(e)",
    "bookDialog.success": "Succès!",
    "bookDialog.appointmentBooked": "Rendez-vous réservé pour le",
    "bookDialog.at": "à",
    "bookDialog.quickBook": "Réservation rapide",
    "bookDialog.bookingFor": "Réservation de",
    "bookDialog.for": "pour",
    "bookDialog.recurring": "Récurrent",
    "bookDialog.recurringNeedsTime": "Choisissez d'abord une heure : c'est elle que la série répète.",
    "bookDialog.recurringNeedsSlots": "Les options de répétition apparaîtront avec les créneaux libres.",
    "bookDialog.recurringNote":
      "Les rendez-vous récurrents sont créés et confirmés immédiatement. Si une seule date est indisponible, toute la série est refusée.",
    "bookDialog.recurringNotePartial":
      "Les rendez-vous récurrents sont créés et confirmés immédiatement. Une date prise par un autre rendez-vous ou par un créneau bloqué est laissée de côté, et le reste de la série est créé.",
    "bookDialog.partialRecurringRefused": "créneau refusé",
    "bookDialog.partialRecurringNothingLeft":
      "Aucune date de cette série ne peut être réservée. Rien n'a été créé.",
    "bookDialog.overtimeWarning": "Ce rendez-vous dépasse l'heure de fermeture",
    "bookDialog.notificationsPaused": "Les notifications de ce salon sont en pause. La cliente ne recevra pas de confirmation pour ce rendez-vous, mais ses rappels partiront normalement.",
    "bookDialog.overtimeDetails": "Le salon ferme à {closingTime}. Cette réservation dépassera de {overtimeMinutes} minutes.",
    "bookDialog.backendConflict.pastClosing": "Ce service se terminerait à {endsAt}, or le salon ferme à {closesAt}. Choisissez une heure plus tôt, ou raccourcissez le service.",
    "bookDialog.backendConflict.crossesMidnight": "Ce service commencerait à {startsAt} et durerait {minutes} minutes, pour se terminer à {endsAt} le lendemain. Un rendez-vous ne peut pas franchir minuit.",
    "bookDialog.backendConflict.blockedFullDay": "Cette journée entière est bloquée pour cet employé. Choisissez une autre journée.",
    "bookDialog.backendConflict.blockedSlot": "Cette heure tombe dans une plage bloquée pour cet employé. Choisissez une autre heure.",
    "bookDialog.backendConflict.overlap": "Cet employé a déjà un rendez-vous qui chevauche cette heure. Choisissez une autre heure ou un autre employé.",
    "bookDialog.backendConflict.exceptionalClosed": "Le salon est exceptionnellement fermé à cette date. Choisissez une autre journée.",
    "bookDialog.backendConflict.beforeOpening": "Cette heure précède l'heure d'ouverture fixée pour cette date exceptionnelle. Choisissez une heure plus tardive.",
    "bookDialog.backendConflict.beforeOpeningHours": "Cette heure précède l'ouverture ({opensAt}). Choisissez une heure plus tardive.",
    "bookDialog.backendConflict.closedDay": "Le salon est fermé ce jour-là. Choisissez une autre journée.",
    "bookDialog.confirmOvertime": "Je confirme cette réservation en heures supplémentaires",
    "bookDialog.outOfHoursWarning": "Ce rendez-vous est en dehors de l'horaire de l'employé",
    "bookDialog.outOfHoursDetails": "{employee} travaille de {start} à {end} ce jour-là.",
    "bookDialog.confirmOutOfHours": "Je confirme ce rendez-vous hors horaire",
    "bookDialog.recurringCreated": "Série récurrente créée avec succès",
    "bookDialog.appointments": "rendez-vous",
    "bookDialog.statusLabel": "Statut",
    "bookDialog.statusConfirmed": "Confirmé",
    "bookDialog.statusPending": "En attente",
    "bookDialog.staffNoteLabel": "Note (facultatif)",
    "bookDialog.staffNotePlaceholder": "Note interne pour ce rendez-vous…",
    "bookDialog.staffNoteVisibleToClient": "Visible par le client",
    "bookDialog.staffNoteVisibleToClientHint": "Si désactivé, la note reste privée à votre équipe.",
    "bookDialog.staffNoteBadgeVisible": "Visible par le client",
    "bookDialog.staffNoteBadgePrivate": "Privée",
    "bookDialog.toast.pending": "Rendez-vous créé en attente pour le",

    // Multi-service booking
    "bookDialog.multiServices": "Multi-Services",
    "bookDialog.multiServicesToggleNo": "Non",
    "bookDialog.forfaitDroppedOnExit": "Ce forfait demande plusieurs services. Quitter le multi-services l'abandonne : choisissez de nouveau les services, ou ajoutez le forfait de nouveau.",
    "bookDialog.multiServicesToggleYes": "Oui",
    "bookDialog.selectedServices": "Services sélectionnés",
    "bookDialog.addService": "Ajouter un service",
    "bookDialog.totalDuration": "Durée totale",
    "bookDialog.totalPrice": "Prix total",
    "bookDialog.servicesCount": "services",
    "bookDialog.variableDurationRequired": "Durée requise",
    "bookDialog.removeService": "Retirer le service",
    "bookDialog.serviceDuration": "Durée (min)",
    "bookDialog.maxServicesReached": "Maximum de 5 services atteint",
    "bookDialog.pause": "Temps de pause",
    "bookDialog.addPause": "Ajouter une pause",
    "bookDialog.removePause": "Retirer la pause",
    "bookDialog.dnd.handleAriaLabel": "Réorganiser cet élément",
    "bookDialog.performedBy": "Fait par",
    "bookDialog.selectEmployeeForService": "Sélectionner",
    "bookDialog.allEmployeesRequired": "Veuillez sélectionner un(e) employé(e) pour chaque service",
    "bookDialog.customerUnresolved": "Le dossier de ce client n'est pas accessible depuis votre compte. Demandez au propriétaire du salon de vous donner accès.",
    "bookDialog.overtimeServiceDetail": "{service} avec {employee} dépasse l'horaire de {minutes} minutes",
    "bookDialog.employeeEndsAt": "{employee} termine à {time}",
    "bookDialog.missingField": "Sélectionnez d'abord : {field}",

    // AppointmentCalendar
    "Calendar.daily": "Jour",
    "Calendar.weekly": "Semaine",
    "Calendar.monthly": "Mois",
    "Calendar.backToView": "Revenir à la vue {view}",
    "Calendar.drawerBusyBook": "Un rendez-vous est en cours de saisie. Terminez-le ou annulez-le avant de bloquer du temps.",
    "Calendar.drawerBusyBlock": "Un blocage de temps est en cours de saisie. Terminez-le ou annulez-le avant d'ouvrir un rendez-vous.",
    "Calendar.slotDuringEditExplain": "Vous modifiez actuellement un rendez-vous. Abandonnez cette modification pour ouvrir le nouveau rendez-vous demandé.",
    "Calendar.slotDuringRebookExplain": "Vous préparez actuellement une pré-réservation. Abandonnez cette pré-réservation pour ouvrir le nouveau rendez-vous demandé.",
    "Calendar.slotDuringEditAbandon": "Abandonner et ouvrir le nouveau rendez-vous",
    "Calendar.cycleViewTitle": "Changer de vue (Jour / Semaine / Mois)",
    "Calendar.today": "Aujourd'hui",
    "Calendar.hairdressers": "Employés",
    "Calendar.room": "Salle :",
    "Calendar.equipment": "Appareil :",
    "Calendar.noAppointments": "Aucun rendez-vous",
    "Calendar.more": "de plus",
    "Calendar.closed": "FERMÉ",
    "Calendar.awayColumnBadge": "Ne travaille pas ce jour-là",
    "Calendar.awayColumnAddedForAppointment": "Ajouté pour ce rendez-vous",
    "Calendar.exceptionalClosure": "FERMETURE EXCEPTIONNELLE",
    "Calendar.serviceBlocksSameDayOnly": "Les blocs de service doivent rester sur la même journée que leur rendez-vous.",
    "Calendar.cannotReschedule": "Impossible de reprogrammer",
    "Calendar.cannotResize": "Impossible de redimensionner",
    "Calendar.invalidTime": "Heure invalide",
    "Calendar.selectTimeBetween7and23": "Veuillez sélectionner une heure entre 7h00 et 23h00",
    "Calendar.appointments": "RDV",
    "Calendar.appointmentRescheduled": "Rendez-vous reprogrammé",
    "Calendar.appointmentResized": "Rendez-vous redimensionné",
    "Calendar.serviceMoved": "Service déplacé",
    "Calendar.serviceMovedSuccess": "Service déplacé avec succès",
    "Calendar.failedToMoveService": "Échec du déplacement du service",
    "Calendar.invalidMove": "Déplacement invalide",
    "Calendar.serviceCannotStartBeforeAppointment": "Le service ne peut pas commencer avant le rendez-vous",
    "Calendar.serviceCannotStartBeforePrevious": "Ce service ne peut pas commencer avant que le service précédent soit terminé",
    "Calendar.serviceCannotEndAfterNext": "Ce service ne peut pas se terminer après le début du service suivant",
    "Calendar.confirmServiceMove": "Confirmer le déplacement",
    "Calendar.confirmServiceMoveDescription": "Déplacer ce service à une nouvelle heure?",
    "Calendar.segmentMoveNotAllowed": "Impossible de déplacer un segment individuel",
    "Calendar.movingEntireAppointment": "Déplacement du rendez-vous complet",
    "Calendar.segmentOverlap": "Cette position chevauche un autre rendez-vous",
    "Calendar.appointmentChangedRetry": "Le rendez-vous vient d'être modifié. Réessayez.",
    "Calendar.overlapWarning": "Ce créneau chevauche un autre rendez-vous",
    "Calendar.outOfHoursWarning": "Ce créneau est en dehors de l'horaire de l'employé",
    "Calendar.outOfHoursMove": "Hors de l'horaire de {employee}",
    "Calendar.moveService": "Déplacer le service",
    "Calendar.confirmServiceTransfer": "Transférer le service à un autre employé",
    "Calendar.confirmServiceTransferDescription": "Transférer ce service à un autre employé? Un nouveau rendez-vous sera créé.",
    "Calendar.transferService": "Transférer le service",
    "Calendar.serviceTransferred": "Service transféré",
    "Calendar.serviceTransferredTo": "Service transféré à {employee}",
    "Calendar.failedToTransferService": "Échec du transfert du service. Veuillez réessayer.",
    "Calendar.serviceTransferNote": "Un nouveau rendez-vous sera créé pour l'employé sélectionné. Le service sera retiré du rendez-vous original.",
    "Calendar.lastServiceCannotMove": "Impossible de transférer le seul service d'un rendez-vous. Déplacez plutôt le rendez-vous entier.",
    "Calendar.cannotMoveService": "Impossible de transférer le service",
    "Calendar.successfullyMoved": "Déplacé avec succès au",
    "Calendar.updated": "Mis à jour de",
    "Calendar.at": "à",
    "Calendar.to": "à",
    "Calendar.from": "De",
    "Calendar.newTime": "Nouvelle heure",
    "Calendar.error": "Erreur",
    "Calendar.failedToReschedule": "Impossible de reprogrammer le rendez-vous. Veuillez réessayer.",
    "Calendar.noServiceFollowsEmployee": "Aucune prestation active n'est attribuée à cet employé, il n'y a donc rien à déplacer.",
    "Calendar.movePartiallyApplied": "Le rendez-vous a été déplacé, mais la réattribution de ses prestations a échoué. Veuillez vérifier le rendez-vous.",
    "Calendar.changeNotAppliedSaveInProgress": "Un enregistrement était en cours : ce changement n'a pas été appliqué. Veuillez refaire le geste.",
    "Calendar.failedToResize": "Impossible de redimensionner le rendez-vous. Veuillez réessayer.",
    "Calendar.serviceWouldOverlapNext": "Ce redimensionnement causerait un chevauchement avec le service suivant.",
    "Calendar.serviceWouldOverlapPrevious": "Ce redimensionnement causerait un chevauchement avec le service précédent.",
    "Calendar.employeeChange": "Changement d'employé",
    "Calendar.reassignedTo": "Réassigné à",
    "Calendar.confirmReschedule": "Confirmer la reprogrammation",
    "Calendar.confirmResize": "Confirmer le redimensionnement",
    "Calendar.confirmRescheduleQuestion": "Êtes-vous sûr de vouloir reprogrammer ce rendez-vous?",
    "Calendar.confirmResizeQuestion": "Êtes-vous sûr de vouloir redimensionner ce rendez-vous?",
    "Calendar.resizeNewAppointmentStart": "Le rendez-vous commencera à {time}.",
    "Calendar.confirmBlockMove": "Déplacer le blocage ?",
    "Calendar.confirmBlockMoveQuestion": "Confirmer le déplacement de ce blocage de temps ?",
    "Calendar.confirmBlockResize": "Modifier la durée du blocage ?",
    "Calendar.confirmBlockResizeQuestion": "Confirmer la nouvelle durée de ce blocage de temps ?",
    "Calendar.service": "Service",
    "Calendar.customer": "Client",
    "Calendar.cancel": "Annuler",
    "Calendar.notifDispatchFailed": "La notification au client n'a pas pu être envoyée.",
    "Calendar.confirm": "Confirmer",
    "Calendar.blockTime": "Bloquer du temps",
    "Calendar.blockedTimeManagement": "Gérer le temps bloqué",
    "Calendar.reason": "Raison",
    "Calendar.add": "Ajouter",
    "Calendar.newAppointment": "Nouveau rendez-vous",
    "Calendar.myCalendarToggle": "Mon calendrier",
    "Calendar.showAllEmployees": "Tous les employés",
    "Calendar.compactView": "Vue compacte",
    "Calendar.expandedView": "Vue élargie",
    "Calendar.quickStats": "Stats rapides",
    "Calendar.weekDays.sun": "DIM",
    "Calendar.weekDays.mon": "LUN",
    "Calendar.weekDays.tue": "MAR",
    "Calendar.weekDays.wed": "MER",
    "Calendar.weekDays.thu": "JEU",
    "Calendar.weekDays.fri": "VEN",
    "Calendar.weekDays.sat": "SAM",
    "Calendar.overflowMenu.period": "Vue",
    "Calendar.overflowMenu.myCalendar": "Mon calendrier",
    "Calendar.overflowMenu.quickStats": "Stats rapides",
    "Calendar.appointmentCount": "Rendez-vous",
    "Calendar.totalRevenue": "Revenu",
    "Calendar.serviceSales": "Ventes services",
    "Calendar.productSales": "Ventes produits",
    "Calendar.averageTicket": "Ticket moyen",
    "Calendar.occupationRate": "Occupation",
    "Calendar.thisWeek": "Cette semaine",
    "Calendar.thisMonth": "Ce mois-ci",
    "Calendar.cancelledNoReschedule": "Les rendez-vous annulés ne peuvent pas être reprogrammés",
    "Calendar.pastNoReschedule": "Les rendez-vous passés ne peuvent pas être reprogrammés",
    "Calendar.salonClosedDay": "Le salon est fermé ce jour-là",
    "Calendar.hairdresserNotWorking": "Cet(te) employé(e) ne travaille pas ce jour-là",
    "Calendar.timeBlocked": "Créneau horaire bloqué",
    "Calendar.timeOverlaps": "Le créneau horaire chevauche un autre rendez-vous",
    "Calendar.employeeAlreadyBooked": "a déjà un rendez-vous à cette heure",
    "Calendar.unknownService": "Service inconnu",
    "Calendar.shareSchedule": "Partager l'horaire",
    "Calendar.editDay": "Modifier",
    "Calendar.viewOnlyEmployee": "Voir seulement {name}",
    "Calendar.viewAllEmployees": "Voir tout le monde",
    "Calendar.showAllStatuses": "Tous les statuts",
    "Calendar.scheduleShared": "Horaire partagé",
    "Calendar.shareError": "Erreur lors du partage",
    "Calendar.clickToBook": "Cliquez pour réserver à",
    "Calendar.slotChoiceAppointment": "Rendez-vous",
    "Calendar.slotChoiceBlock": "Blocage de temps",
    "Calendar.slotChoiceAppointmentHint": "Client, prestation, heure",
    "Calendar.slotChoiceBlockHint": "Congé, dîner, formation",
    "Calendar.slotChoiceTitle": "Que voulez-vous ajouter ?",
    "Calendar.blockRoom": "Bloquer cette salle",
    "Calendar.blockRoomDescription": "La salle sera fermée sur la plage choisie. Aucun rendez-vous ne pourra y être pris, ni depuis le tableau de bord, ni depuis la réservation en ligne.",
    "Calendar.notes": "Notes:",
    "Calendar.pauseOccupied": "Période de pause (Occupée)",
    "Calendar.pauseBookedMsg": "Fait partie du rendez-vous : réservation en cours",
    "Calendar.availableSlot": "Créneau disponible",
    "Calendar.partOfAppointment": "Fait partie du rendez-vous",
    "Calendar.clickDuringPause": "Cliquez pour réserver pendant cette pause",
    "Calendar.workBlock": "Bloc de travail",
    "Calendar.rescheduleSuccess": "Rendez-vous reprogrammé avec succès",
    "Calendar.rescheduleError": "Échec de la reprogrammation du rendez-vous",
    "Calendar.resizeSuccess": "Durée du rendez-vous modifiée avec succès",
    "Calendar.resizeError": "Échec de la modification de la durée du rendez-vous",
    "Calendar.editAppointment": "Modifier le rendez-vous",
    "Calendar.hairdresser": "Employé",
    "Calendar.selectHairdresser": "Sélectionner un employé",
    "Calendar.date": "Date",
    "Calendar.duration": "Durée",
    "Calendar.time": "Heure",
    "Calendar.save": "Enregistrer",
    "Calendar.saving": "Enregistrement...",
    "Calendar.success": "Succès",
    "Calendar.appointmentUpdated": "Rendez-vous mis à jour avec succès",
    "Calendar.noAvailableSlots": "Aucun créneau disponible",
    "Calendar.fillAllFields": "Veuillez remplir tous les champs obligatoires",
    "Calendar.errorFetchingSlots": "Impossible de récupérer les créneaux disponibles",
    "Calendar.errorUpdating": "Impossible de mettre à jour le rendez-vous",
    "Calendar.multiServiceAppointment": "Rendez-vous multi-services",
    "Calendar.mobileMove": "Déplacer",
    "Calendar.mobileMoveService": "Déplacer ce service",
    "Calendar.mobileMoveSegment": "Déplacer ce bloc",
    "Calendar.mobileDuration": "Durée",
    "Calendar.mobileDurationService": "Durée de ce service",
    "Calendar.mobileDurationSegment": "Durée de ce bloc",
    "Calendar.mobileAdjust": "Ajuster",
    "Calendar.mobileWas": "était",
    "Calendar.mobileSelected": "Sélectionné",
    "Calendar.mobileScopeServiceSubtitle": "Service {index} sur {total} : {name}",
    "Calendar.mobileScopeSegmentSubtitle": "Bloc {index} sur {total} : {name}",
    "Calendar.mobileEditFullAppointment": "Modifier le rdv complet",
    "Calendar.mobileCancelFullAppointment": "Annuler le rdv complet",
    "Calendar.employees": "Employés",
    "Calendar.appointmentStatus": "Statut du rendez-vous",
    "Calendar.weekJump": "Semaines",
    "Calendar.statusConfirmed": "Confirmé",
    "Calendar.statusPending": "En attente",
    "Calendar.statusCancelled": "Annulé",
    "Calendar.statusNoShow": "No-show",
    "Calendar.statusPresent": "Présent",
    "Calendar.toggleSidebar": "Panneau latéral",
    "Calendar.groupByEmployee": "Par employé",
    "Calendar.groupByRoom": "Par salle",
    "Calendar.unassignedRoom": "Non assigné",
    "Calendar.moveToRoom": "Salle",
    "Calendar.roomChangeRefused": "La salle n'a pas pu être changée ({reason}). Le rendez-vous reste dans sa salle d'origine.",
    "Calendar.workstationsShort": "postes",

    // BlockedSlots
    "BlockedSlots.addTitle": "Bloquer une plage horaire",
    "BlockedSlots.editTitle": "Modifier le temps bloqué",
    "BlockedSlots.dialogDescription":
      "Bloquez une journée entière ou une ou plusieurs plages horaires, pour tout le salon ou pour certains employés.",
    "BlockedSlots.hairdresser": "Employé(e)",
    "BlockedSlots.allHairdressers": "Tous les employés (Salon complet)",
    "BlockedSlots.allHairdressersCheckbox": "Tout le salon",
    "BlockedSlots.selectAtLeastOne": "Sélectionnez au moins un employé",
    "BlockedSlots.date": "Date",
    "BlockedSlots.pickDate": "Choisir une date",
    "BlockedSlots.timeSlots": "Plages horaires",
    "BlockedSlots.addTimeSlot": "Ajouter une plage",
    "BlockedSlots.slot": "Plage",
    "BlockedSlots.startTime": "Heure de début",
    "BlockedSlots.endTime": "Heure de fin",
    "BlockedSlots.selectTime": "Sélectionner l'heure",
    "BlockedSlots.reason": "Raison",
    "BlockedSlots.optional": "optionnel",
    "BlockedSlots.reasonPlaceholder": "ex: Rendez-vous médical, Formation, etc.",
    "BlockedSlots.fillAllFields": "Veuillez remplir tous les champs requis",
    "BlockedSlots.invalidTimeRange": "L'heure de fin doit être après l'heure de début",
    "BlockedSlots.closedDay": "Le salon/employé est fermé ce jour-là",
    "BlockedSlots.outsideHours": "L'heure sélectionnée est en dehors des heures de travail. Heures valides :",
    "BlockedSlots.createSuccess": "Plage horaire bloquée avec succès",
    "BlockedSlots.createSuccessMultiple": "plages horaires bloquées avec succès",
    "BlockedSlots.updateSuccess": "Temps bloqué mis à jour",
    "BlockedSlots.deleteSuccess": "Temps bloqué supprimé",
    "BlockedSlots.saveError": "Échec de l'enregistrement du temps bloqué",
    "BlockedSlots.deleteError": "Échec de la suppression du temps bloqué",
    "BlockedSlots.allLocationsNote": "Ce blocage s'applique dans toutes ses succursales.",
    "BlockedSlots.notAppliedIn": "Ce blocage n'a pas pu être appliqué dans : {locations}.",
    "BlockedSlots.checkAccess": "Vérifiez vos accès à ces succursales.",
    "BlockedSlots.copiesNotChecked": "Les autres succursales de cette personne n'ont pas pu être vérifiées : ce blocage n'y est peut-être pas appliqué.",
    "BlockedSlots.siblingAppointmentsWarning": "Cette personne a {count} rendez-vous dans ses autres succursales pendant ce blocage. Voulez-vous quand même bloquer ?",
    "BlockedSlots.moveSuccess": "Blocage déplacé",
    "BlockedSlots.moveError": "Erreur lors du déplacement du blocage",
    "BlockedSlots.resizeSuccess": "Durée du blocage mise à jour",
    "BlockedSlots.resizeError": "Erreur lors du redimensionnement du blocage",
    "BlockedSlots.overlapRefused": "Impossible : ce blocage chevaucherait un rendez-vous",
    "BlockedSlots.conflictCheckError": "Impossible de vérifier les rendez-vous. Vérifiez votre connexion et réessayez.",
    "BlockedSlots.invalidRange": "Plage horaire invalide",
    "BlockedSlots.blocked": "Bloqué",
    "BlockedSlots.blocksInRange": "blocages sur cette plage",
    "BlockedSlots.blocksCount": "blocages",
    "BlockedSlots.noReason": "Sans motif",
    "BlockedSlots.salonClosed": "Salon fermé",
    "BlockedSlots.blockedTime": "Plage horaire bloquée",
    "BlockedSlots.salonBlocked": "Blocage du salon complet",
    "BlockedSlots.fullDay": "Bloquer toute la journée",
    "BlockedSlots.fullDayDescription": "Bloque automatiquement toutes les heures de travail",
    "BlockedSlots.willBlock": "Bloquera :",
    "BlockedSlots.willBlockMultiple": "Bloquera pour chaque employé :",
    "BlockedSlots.recurring": "Récurrent",
    "BlockedSlots.recurringDescription": "Créer des blocages récurrents sur plusieurs dates",
    "BlockedSlots.repeatEvery": "Répéter chaque",
    "BlockedSlots.week": "Semaine",
    "BlockedSlots.weeks": "Semaines",
    "BlockedSlots.month": "Mois",
    "BlockedSlots.months": "Mois",
    "BlockedSlots.untilDate": "Jusqu'au",
    "BlockedSlots.pickEndDate": "Choisir une date de fin",
    "BlockedSlots.maxDurationAhead": "Maximum {duration} à l'avance",
    "BlockedSlots.blockWillBeCreated": "blocage sera créé",
    "BlockedSlots.blocksWillBeCreated": "blocages seront créés",
    "BlockedSlots.groupManagement": "Groupe de blocages",
    "BlockedSlots.groupDialogDescription":
      "Toutes les lignes de ce groupe. Sélectionnez celles à retirer, ou modifiez-en une seule.",
    "BlockedSlots.employeesBlocked": "Employés bloqués",
    "BlockedSlots.selectAllForDay": "Sélectionner toute la journée",
    "BlockedSlots.deleteSelected": "Supprimer sélection",
    "BlockedSlots.deleteSuccessMultiple": "temps bloqués supprimés",
    "BlockedSlots.confirmDeleteTitle": "Confirmer la suppression",
    "BlockedSlots.confirmDeleteSelectedDescription": "Êtes-vous sûr de vouloir supprimer les blocages sélectionnés ?",
    "BlockedSlots.heterogeneousWarning": "Les heures diffèrent d'une ligne à l'autre. Enregistrer appliquera les mêmes heures à toutes.",
    "BlockedSlots.startDate": "Du",
    "BlockedSlots.endDate": "Au",
    "BlockedSlots.multiDayPreview": "{count} jours seront bloqués",
    "BlockedSlots.multiDaySummary": "{count} jours bloqués, du {start} au {end}",
    "BlockedSlots.dateRangeWarning": "Certains jours de cette période ont des rendez-vous existants",
    "BlockedSlots.advancedOptions": "Options avancées",
    "BlockedSlots.repeatLabel": "Répétition",
    "BlockedSlots.repeatSingle": "Ce jour seulement",
    "BlockedSlots.repeatConsecutive": "Jours consécutifs",
    "BlockedSlots.repeatRecurring": "Récurrence",
    "BlockedSlots.deleteConfirmTitle": "Supprimer ce blocage?",
    "BlockedSlots.deleteConfirmDescription": "Cette action est irréversible.",

    // Calendrier
    "Calendar.showAll": "Tous les employés",
    "Calendar.myCalendar": "Mon calendrier",

    // Daily Notes
    "DailyNote.title": "Note du jour",
    "DailyNote.placeholder": "ex: Arrivée tardive, Enfant malade...",
    "DailyNote.save": "Enregistrer",
    "DailyNote.clear": "Effacer",
    "DailyNote.saved": "Note enregistrée",
    "DailyNote.cleared": "Note effacée",
    "DailyNote.error": "Échec de l'enregistrement",
    "Calendar.colorByRoom": "Par salle",
    "pendingDialog.expiredTitle": "Demandes expirées",
    "pendingDialog.expiredHint": "Ces rendez-vous sont passés et ne peuvent plus être approuvés. Ils ne sont pas comptés dans la pastille.",
    "pendingDialog.expiredDismiss": "Refuser",
    "cancellationLog.windowHint": "Annulations des 90 derniers jours que vous n'avez pas marquées comme vues. L'historique complet se trouve dans les Rapports.",
    "cancellationLog.loadFailed": "Impossible de charger les annulations. Veuillez réessayer.",

    // Cancel
    "cancel.noteLabel": "Note d'annulation",
    "cancel.notePlaceholder": "Raison de l'annulation ou note de suivi...",
    "cancel.noteVisibleToClient": "Visible par le client (inclus dans le courriel d'annulation)",

    // Cancellation Log
    "cancellationLog.title": "Rendez-vous annulés",
    "cancellationLog.markAsSeen": "Marquer comme vu",
    "cancellationLog.markAllAsSeen": "Tout marquer comme vu",
    "cancellationLog.noItems": "Aucune annulation non acquittée",
    "cancellationLog.cancelledBy": "Annulé par",
    "cancellationLog.cancelledAt": "Annulé le",
    "cancellationLog.internalNote": "Note interne",
    "cancellationLog.cancelledByClient": "client",
    "cancellationLog.cancelledByOwner": "propriétaire",
    "cancellationLog.cancelledByEmployee": "employé",
    "cancellationLog.cancelledByVoiceAI": "assistant vocal",
    "cancellationLog.byNamed": "Annulé par {name} ({role})",
    "cancellationLog.byOwner": "Annulé par le propriétaire",
    "cancellationLog.byEmployee": "Annulé par un employé",
    "cancellationLog.byClient": "Annulé par le client",
    "cancellationLog.byVoiceAI": "Annulé par l'assistant vocal",
    "cancellationLog.bySystem": "Annulé automatiquement (dépôt non reçu à temps)",
    "cancellationLog.byCascade": "Annulé par le salon (dernier service retiré)",
    "cancellationLog.byRaw": "Annulé par {actor}",
    "cancellationLog.whoNamed": "{name} ({role})",
    "cancellationLog.whoOwner": "Propriétaire",
    "cancellationLog.whoEmployee": "Employé",
    "cancellationLog.whoClient": "Client",
    "cancellationLog.whoVoiceAI": "Assistant vocal",
    "cancellationLog.whoSystem": "Automatique (dépôt non reçu à temps)",
    "cancellationLog.whoCascade": "Salon (dernier service retiré)",
    "cancellationLog.roleOwner": "propriétaire",
    "cancellationLog.roleEmployee": "employé",
    "cancellationLog.roleClient": "client",
    "cancellationLog.showOnCalendar": "Réafficher sur le calendrier",
    "cancellationLog.shownOnCalendar": "Rendez-vous réaffiché sur le calendrier",
    "cancellationLog.hiddenBadge": "Masqué du calendrier",
    "cancellationLog.showOnCalendarFailed": "Impossible de réafficher le rendez-vous. Réessayez.",

    // Booking drawer shell
    "Calendar.drawerClose": "Fermer",
    "Calendar.drawerPeek": "Aperçu",
    "Calendar.drawerHalf": "Moitié d'écran",
    "Calendar.drawerFull": "Plein écran",
  },
};
